import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { getScenarioId } from '@/Config/auth/authStore';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { SecretInput } from '@/Domain/components/Shared/Input/SecretInput';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import CloseIcon from '@/Domain/components/icons/Close.icon';

interface ZendeskCredentials {
  app_id: string;
  api_key_id: string;
  api_key_secret: string;
}

interface ZendeskSettings {
  active: boolean;
  is_default: boolean;
  handoff: boolean;
}

type ZendeskIntegration = ZendeskCredentials & ZendeskSettings;

interface ZendeskSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_CREDENTIALS: ZendeskCredentials = {
  app_id: '',
  api_key_id: '',
  api_key_secret: '',
};

const EMPTY_SETTINGS: ZendeskSettings = {
  active: false,
  is_default: false,
  handoff: false,
};

const isConnected = (data: unknown): data is ZendeskIntegration =>
  !!data && typeof data === 'object' && 'app_id' in data && !!(data as ZendeskIntegration).app_id;

export const ZendeskSheet = ({ open, onOpenChange }: ZendeskSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const scenarioId = getScenarioId();

  const queryKey = [QueryKey.integration.zendeskGet, scenarioId] as const;

  const { data, isLoading } = useQuery<ZendeskIntegration | Record<string, never>>({
    queryKey,
    enabled: open && !!scenarioId,
  });

  const connected = isConnected(data);
  const credentials: ZendeskCredentials = connected
    ? {
        app_id: data.app_id ?? '',
        api_key_id: data.api_key_id ?? '',
        api_key_secret: data.api_key_secret ?? '',
      }
    : EMPTY_CREDENTIALS;
  const settings: ZendeskSettings = connected
    ? {
        active: Boolean(data.active),
        is_default: Boolean(data.is_default),
        handoff: Boolean(data.handoff),
      }
    : EMPTY_SETTINGS;

  const tag = `quickchatai-bot-${scenarioId}`;

  const [form, setForm] = useState<ZendeskCredentials>(EMPTY_CREDENTIALS);

  // When the sheet opens or fresh server data arrives, sync the form so the
  // disconnected view shows the (empty) credentials state and the connected
  // view shows the persisted ones.
  useEffect(() => {
    if (!open) return;
    setForm(connected ? credentials : EMPTY_CREDENTIALS);
    // credentials is derived from `data`; depending on `data` is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  const connectMutation = useMutation<
    ZendeskIntegration,
    unknown,
    { scenarioId: string; body: ZendeskCredentials }
  >({
    mutationKey: [QueryKey.integration.zendeskCreate],
    onSuccess: (responseData) => {
      queryClient.setQueryData(queryKey, responseData);
      toast.success(t('integration:zendesk.connected'));
    },
    onError: () => toast.error(t('integration:zendesk.connect_failed')),
  });

  const disconnectMutation = useMutation<unknown, unknown, { scenarioId: string }>({
    mutationKey: [QueryKey.integration.zendeskDelete],
    onSuccess: () => {
      queryClient.setQueryData(queryKey, {});
      toast.success(t('integration:zendesk.disconnected'));
    },
    onError: () => toast.error(t('integration:zendesk.disconnect_failed')),
  });

  const patchMutation = useMutation<
    ZendeskIntegration,
    unknown,
    { scenarioId: string; body: ZendeskSettings }
  >({
    mutationKey: [QueryKey.integration.zendeskPatch],
    onSuccess: (responseData) => {
      queryClient.setQueryData(queryKey, responseData);
    },
    onError: () => toast.error(t('integration:zendesk.update_failed')),
  });

  const handleConnect = useCallback(() => {
    connectMutation.mutate({ scenarioId, body: form });
  }, [connectMutation, scenarioId, form]);

  const handleDisconnect = useCallback(() => {
    disconnectMutation.mutate({ scenarioId });
  }, [disconnectMutation, scenarioId]);

  const handleActiveToggle = useCallback(
    (checked: boolean) => {
      const next: ZendeskSettings = checked
        ? { ...settings, active: true }
        : { ...settings, active: false, is_default: false };
      patchMutation.mutate({ scenarioId, body: next });
    },
    [scenarioId, settings, patchMutation],
  );

  const handleSettingToggle = useCallback(
    (key: 'is_default' | 'handoff', checked: boolean) => {
      patchMutation.mutate({ scenarioId, body: { ...settings, [key]: checked } });
    },
    [scenarioId, settings, patchMutation],
  );

  const isSaving = connectMutation.isPending || disconnectMutation.isPending;
  const isFormValid = form.app_id.trim() && form.api_key_id.trim() && form.api_key_secret.trim();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl w-full h-full" data-testid="zendesk-sheet">
        <SheetHeader className="px-0.5">
          <div className="flex flex-col gap-1">
            <SheetTitle data-testid="zendesk-sheet-title">
              {t('integration:zendesk.title')}
            </SheetTitle>
            <SheetDescription data-testid="zendesk-sheet-description">
              {t('integration:zendesk.description')}{' '}
              <a
                href="https://docs.quickchat.ai/Channels/External%20Apps/zendesk"
                target="_blank"
                rel="noreferrer"
                className="underline font-medium"
              >
                {t('integration:zendesk.guide_link')}
              </a>
              .
            </SheetDescription>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="zendesk-sheet-close-button"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="flex flex-col gap-6 overflow-y-auto px-0.5 flex-1">
          {isLoading ? (
            <div
              className="flex items-center justify-center py-12 text-muted-foreground"
              data-testid="zendesk-loading"
            >
              {t('integration:zendesk.loading')}
            </div>
          ) : !connected ? (
            <div className="flex flex-col gap-4" data-testid="zendesk-disconnected-view">
              <p className="text-sm text-muted-foreground m-0">
                {t('integration:zendesk.setup_hint')}
              </p>
              <div className="flex flex-col gap-2">
                <Label htmlFor="zendesk-app-id">{t('integration:zendesk.app_id_label')}</Label>
                <Input
                  id="zendesk-app-id"
                  data-testid="zendesk-app-id-input"
                  placeholder={t('integration:zendesk.app_id_placeholder')}
                  value={form.app_id}
                  onChange={(e) => setForm({ ...form, app_id: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="zendesk-api-key-id">
                  {t('integration:zendesk.api_key_id_label')}
                </Label>
                <Input
                  id="zendesk-api-key-id"
                  data-testid="zendesk-api-key-id-input"
                  placeholder={t('integration:zendesk.api_key_id_placeholder')}
                  value={form.api_key_id}
                  onChange={(e) => setForm({ ...form, api_key_id: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="zendesk-api-key-secret">
                  {t('integration:zendesk.api_key_secret_label')}
                </Label>
                <SecretInput
                  id="zendesk-api-key-secret"
                  data-testid="zendesk-api-key-secret-input"
                  placeholder={t('integration:zendesk.api_key_secret_placeholder')}
                  value={form.api_key_secret}
                  onChange={(e) => setForm({ ...form, api_key_secret: e.target.value })}
                />
              </div>
              <div className="flex items-center pt-2">
                <Button
                  variant="dark"
                  onClick={handleConnect}
                  disabled={isSaving || !isFormValid}
                  data-testid="zendesk-connect-button"
                >
                  {connectMutation.isPending
                    ? t('integration:zendesk.connecting')
                    : t('integration:zendesk.authenticate')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4" data-testid="zendesk-connected-view">
              <div
                className="flex items-start gap-3 rounded-xl border border-success-muted bg-success-muted p-4"
                data-testid="zendesk-connected-banner"
              >
                <p className="text-sm text-success m-0 flex-1">
                  {t('integration:zendesk.connected_banner')}
                </p>
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col">
                  <Label htmlFor="zendesk-active-toggle" className="cursor-pointer">
                    {t('integration:zendesk.active_label')}
                  </Label>
                  <p className="text-xs text-muted-foreground m-0">
                    {t('integration:zendesk.active_hint')}
                  </p>
                </div>
                <Switch
                  id="zendesk-active-toggle"
                  data-testid="zendesk-active-toggle"
                  checked={settings.active}
                  onCheckedChange={handleActiveToggle}
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col">
                  <Label htmlFor="zendesk-is-default-toggle" className="cursor-pointer">
                    {t('integration:zendesk.is_default_label')}
                  </Label>
                  <p className="text-xs text-muted-foreground m-0">
                    {t('integration:zendesk.is_default_hint')}
                  </p>
                </div>
                <Switch
                  id="zendesk-is-default-toggle"
                  data-testid="zendesk-is-default-toggle"
                  checked={settings.is_default}
                  onCheckedChange={(checked) => handleSettingToggle('is_default', checked)}
                  disabled={!settings.active}
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col">
                  <Label htmlFor="zendesk-handoff-toggle" className="cursor-pointer">
                    {t('integration:zendesk.handoff_label')}
                  </Label>
                  <p className="text-xs text-muted-foreground m-0">
                    {t('integration:zendesk.handoff_hint')}
                  </p>
                </div>
                <Switch
                  id="zendesk-handoff-toggle"
                  data-testid="zendesk-handoff-toggle"
                  checked={settings.handoff}
                  onCheckedChange={(checked) => handleSettingToggle('handoff', checked)}
                  disabled={!settings.active}
                />
              </div>

              <Separator />

              <div className="flex flex-col gap-2">
                <Label>{t('integration:zendesk.tag_label')}</Label>
                <code
                  className="rounded-md border border-border bg-muted p-3 text-sm font-mono text-foreground"
                  data-testid="zendesk-tag"
                >
                  {tag}
                </code>
                <p className="text-xs text-muted-foreground m-0">
                  {t('integration:zendesk.tag_hint')}
                </p>
              </div>

              <Separator />

              <div className="flex items-center pt-2">
                <Button
                  variant="danger"
                  onClick={handleDisconnect}
                  disabled={isSaving}
                  data-testid="zendesk-disconnect-button"
                >
                  {disconnectMutation.isPending
                    ? t('integration:zendesk.disconnecting')
                    : t('integration:zendesk.disconnect')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
